import React from 'react';
import { Link, NavLink } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { capitalize, upperCase } from 'lodash';

import { useReservationLimitInPeriodContext } from '../../contexts/ReservationsLimitInPeriodContext';
import { Plan } from '../../pages/SubscriptionPlans/types';
import Button from '../Button/Button';
import { PlusIcon } from '../Dialogs/CollabsFilters/ToolTip';
import { useAppSelector } from '../../redux/hooks';

type Props = {
  totalReservations: number;
  plan?: Plan;
  limit?: number;
  fitContent?: boolean;
  onClick?: () => void;
  isActive?: boolean;
};

export const BusinessMonthlyReservationLimit: React.FC<Props> = ({
  totalReservations = 0,
  plan,
  limit,
  fitContent = false,
  onClick,
  isActive = false,
}) => {
  const reservationLimitContext = useReservationLimitInPeriodContext();
  const periodEndDate = reservationLimitContext?.periodEndDate;
  const venueState = useAppSelector(state => state.venue.value);

  const planLabel = plan === 'plus' ? upperCase(plan) : capitalize(plan || 'free');
  const effectiveLimit = limit ?? venueState?.totalAllowedPromotions ?? 0;
  const showUpgradeButton =
    !!effectiveLimit &&
    (venueState?.totalConsumedPromotions ?? 0) >= effectiveLimit &&
    plan !== 'plus';


  return (
    <Container
      fitContent={fitContent && !showUpgradeButton}
      clickable={!!onClick}
      isActive={isActive}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Box display={'flex'} alignItems={'center'} gap={4.5}>
        <Limit>
          {(venueState?.totalConsumedPromotions ?? 0)}
          {plan !== 'plus' && !!effectiveLimit && `/${effectiveLimit}`}
        </Limit>
        <Box>
          <Typography sx={{ fontSize: '18px', color: '#232832', fontWeight: 600 }}>
            Monthly Promotions
          </Typography>
          <Typography sx={{ color: '#1A1C1E', fontSize: '15px' }}>
            {plan === 'plus' ? (
              <>
                Unlimited promotions included in your{' '}
                <b>
                  <NavLink
                    style={{ color: '#1A1C1E', textDecoration: 'underline' }}
                    to={'/plans'}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {planLabel} Plan
                  </NavLink>
                </b>
                . Resets on {dayjs(periodEndDate).format('MMMM D, YYYY')}.
              </>
            ) : effectiveLimit && totalReservations >= effectiveLimit ? (
              <>
                Update your plan to unlock more promotions or wait until{' '}
                {dayjs(periodEndDate).format('MMMM D, YYYY')}.
              </>
            ) : (
              <>
                Included in your{' '}
                <b>
                  <NavLink
                    style={{ color: '#1A1C1E', textDecoration: 'underline' }}
                    to={'/plans'}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {planLabel} Plan
                  </NavLink>
                </b>
                . Resets on {dayjs(periodEndDate).format('MMMM D, YYYY')}.
              </>
            )}
          </Typography>
        </Box>
      </Box>
      {showUpgradeButton && (
        <Box onClick={(e) => e.stopPropagation()}>
          <Button
            component={Link}
            variant='contained'
            color='primary'
            sx={{ px: 2.2, py: 0.8 }}
            startIcon={<PlusIcon sx={{ height: '16px', width: '16px' }} />}
            to='/plans'
          >
            Upgrade plan
          </Button>
        </Box>
      )}
    </Container>
  );
};

const Container = styled.div<{
  fitContent?: boolean;
  clickable?: boolean;
  isActive?: boolean;
}>`
  border-radius: 8px;
  border: ${({ isActive }) => (isActive ? '2px solid #232832' : '1px solid #f3f3f3')};
  background-color: ${({ isActive }) => (isActive ? '#f3f4f6' : '#fafafa')};
  box-shadow: ${({ isActive }) =>
    isActive ? '0 0 0 4px rgba(35, 40, 50, 0.08)' : 'none'};

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: ${({ isActive }) => (isActive ? '21px 27px' : '22px 28px')};
  width: ${({ fitContent }) => (fitContent ? 'fit-content' : '100%')};
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  transition: border-color 0.15s ease, background-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ clickable, isActive }) =>
      clickable ? '#232832' : isActive ? '#232832' : '#f3f3f3'};
  }
`;

const Limit = styled.h1`
  color: #232832;
  font-family: Poppins;
  font-size: 40px;
  font-weight: 600;
  margin: 0;
  padding: 0;
`;
